import { useMemo, useState } from 'react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { ScrollArea } from '@/components/ui/scroll-area'
import { ChevronDown, Search } from 'lucide-react'
import { FEATURE_ICON_NAMES, resolveFeatureIcon } from '@/lib/schoolFeatures'

interface IconPickerProps {
  value: string
  onChange: (icon: string) => void
  className?: string
}

// Seletor de ícone (lucide) para selos customizados. Mostra um grid pesquisável
// dos ícones do FEATURE_ICON_REGISTRY.
export function IconPicker({ value, onChange, className }: IconPickerProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')

  const SelectedIcon = resolveFeatureIcon(value)

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return FEATURE_ICON_NAMES
    return FEATURE_ICON_NAMES.filter((name) => name.toLowerCase().includes(q))
  }, [query])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className={cn('shrink-0', className)}
          aria-label="Escolher ícone"
        >
          <SelectedIcon className="h-4 w-4" />
          <ChevronDown className="h-3 w-3 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-2">
        <div className="relative mb-2">
          <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar ícone..."
            className="h-8 pl-7 text-sm"
          />
        </div>
        <ScrollArea className="h-48">
          <div className="grid grid-cols-6 gap-1 pr-2">
            {filtered.map((name) => {
              const Icon = resolveFeatureIcon(name)
              return (
                <button
                  key={name}
                  type="button"
                  title={name}
                  onClick={() => {
                    onChange(name)
                    setOpen(false)
                  }}
                  className={cn(
                    'flex h-8 w-8 items-center justify-center rounded-md border transition-colors',
                    value === name
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-transparent text-muted-foreground hover:bg-accent',
                  )}
                >
                  <Icon className="h-4 w-4" />
                </button>
              )
            })}
            {filtered.length === 0 && (
              <p className="col-span-6 py-4 text-center text-xs text-muted-foreground">
                Nenhum ícone encontrado.
              </p>
            )}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  )
}
